import React, { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;
const API_URL = process.env.REACT_APP_BACKEND_URL.replace(/^http:\/\//i, 'https://');

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

const PushPermissionPrompt = () => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const dismissed = localStorage.getItem('push_prompt_dismissed');
    const alreadySubscribed = localStorage.getItem('push_subscribed');

    if (!token || dismissed || alreadySubscribed) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (Notification.permission === 'granted') return;
    if (Notification.permission === 'denied') return;

    // Show prompt after 3 seconds
    const timer = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleAccept = async () => {
    setLoading(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setVisible(false);
        localStorage.setItem('push_prompt_dismissed', 'true');
        return;
      }

      // Register service worker and subscribe
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      // Send subscription to backend
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/notifications/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subscription: subscription.toJSON() }),
      });

      localStorage.setItem('push_subscribed', 'true');
      setVisible(false);
    } catch (e) {
      console.error('Push subscription failed:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('push_prompt_dismissed', 'true');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="push-permission-prompt"
      className="fixed top-4 right-4 z-[80] max-w-sm"
      style={{ animation: 'slideIn 0.3s ease-out' }}
    >
      <div className="bg-[#1E1E1E] border border-[#00BFA6]/30 rounded-xl p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#00BFA6]/10 flex items-center justify-center shrink-0">
            <Bell className="w-5 h-5 text-[#00BFA6]" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-white mb-1">Push-Benachrichtigungen</h3>
            <p className="text-xs text-gray-400 mb-3 leading-relaxed">
              Erhalte sofort Bescheid bei neuen High-Confidence-Tips, Ergebnissen und deinem taeglichen Value-Bet Digest.
            </p>
            <div className="flex gap-2">
              <Button
                data-testid="push-accept-btn"
                onClick={handleAccept}
                disabled={loading}
                className="bg-[#00BFA6] hover:bg-[#009E8A] text-black text-xs font-bold px-4 py-1.5 h-auto"
              >
                {loading ? 'Aktiviert...' : 'Ja, aktivieren'}
              </Button>
              <Button
                data-testid="push-dismiss-btn"
                onClick={handleDismiss}
                variant="ghost"
                className="text-gray-400 hover:text-white text-xs px-3 py-1.5 h-auto"
              >
                Spaeter
              </Button>
            </div>
          </div>
          <button onClick={handleDismiss} className="text-gray-500 hover:text-white p-1">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default PushPermissionPrompt;
